// useAuth.ts — Authentication hook for the openscan SPA (cooking's pattern).
//
// Auth is an authz.lab0.us session held in an HttpOnly cookie that the Go
// server sets after the OIDC round-trip (lib/oidcrp). In local-dev mode (no
// OIDC_CLIENT_SECRET) the server answers /api/me as dev@local with no cookie.
// The browser never handles a token: /api/me reports the current identity,
// and signing in is a redirect to the server-side /api/oidc/login.
import { useCallback, useEffect, useState } from 'react';

import { apiClient } from '../api';

export interface AuthState {
  email: string;
  name: string;
  /** Avatar URL from the authz profile; empty when the issuer has none. */
  photoUrl: string;
  roles: string[];
  scopes: string[];
  // library: may create and list own scans (openscan.user); compute: may run
  // refinements (openscan.compute). Both false when signed out.
  capabilities: { library: boolean; compute: boolean };
  loading: boolean;
  error: string;
  authenticated: boolean;
}

const NO_CAPABILITIES = { library: false, compute: false };

// capabilitiesOf reads /api/me's capabilities. A server that predates them
// had no guests and no compute gate, so a signed-in user could do both.
function capabilitiesOf(data: { capabilities?: { library?: boolean; compute?: boolean } }): { library: boolean; compute: boolean } {
  if (!data.capabilities) return { library: true, compute: true };
  return { library: !!data.capabilities.library, compute: !!data.capabilities.compute };
}

// useAuth reports the current user, or an unauthenticated state when no valid
// session cookie is present.
export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({
    email: '', name: '', photoUrl: '', roles: [], scopes: [], capabilities: NO_CAPABILITIES, loading: true, error: '', authenticated: false,
  });

  const fetchMe = useCallback(async () => {
    try {
      const res = await apiClient.fetch('/api/me');
      if (res.status === 401 || res.status === 403) {
        // Signed out is a normal state (a guest on a share link), not an error.
        console.info('[openscan/auth] not signed in');
        setState((prev) => ({ ...prev, capabilities: NO_CAPABILITIES, loading: false, error: '', authenticated: false }));
        return;
      }
      if (!res.ok) throw new Error(`Failed to load profile (${res.status})`);
      const data = await res.json();
      console.info('[openscan/auth] signed in');
      setState({
        email: data.email || '',
        name: data.name || '',
        photoUrl: data.photo_url || data.photoUrl || '',
        roles: data.roles || [],
        scopes: data.scopes || [],
        capabilities: capabilitiesOf(data),
        loading: false,
        error: '',
        authenticated: true,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to load profile';
      console.warn('[openscan/auth]', msg);
      setState((prev) => ({ ...prev, loading: false, error: msg, authenticated: false }));
    }
  }, []);

  useEffect(() => {
    void fetchMe();
  }, [fetchMe]);

  return state;
}

// redirectToLogin hands off to the server-side OIDC flow, which bounces to
// authz and returns to the current path (never to /login, which would loop).
export function redirectToLogin(): void {
  const path = window.location.pathname + window.location.search;
  const returnTo = window.location.pathname.startsWith('/login') ? '/' : path;
  window.location.href = `/api/oidc/login?return_to=${encodeURIComponent(returnTo)}`;
}

// logout clears the session cookie and reloads.
export function logout(): void {
  window.location.href = '/api/oidc/logout';
}
